/**
 * Candidates — everybody who has not been ruled out, and finding them by what they can do.
 *
 * WHY THIS SCREEN WAS EMPTY. It listed `TfTalentProfiles`, and nothing created one. Ten people in
 * the system, none with a talent profile, so nothing to show and nothing saying why. Candidate
 * status now lives on the relationship, so every person can be a candidate without another record
 * having to exist first.
 *
 * WHO IS ON THE LIST. Everybody except **Not Looking** and **Not a Candidate**. Somebody nobody has
 * triaged yet is included deliberately — a recruiter can rule people out, but cannot rule in a
 * person they never knew was there. The count of untriaged people is shown so that inclusiveness
 * stays visible rather than becoming clutter nobody understands.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { tfApi } from '../api/tf_api'
import { useApp } from '../contexts/app_context'
import { PageHeader } from '../components/layout/app_shell'
import { InlineEditCell } from '../components/ui/inline_edit_cell'
import { Avatar, EmptyState, ErrorState, Spinner, Tag } from '../components/ui/primitives'
import type { CandidateRow, SkillCatalogEntry } from '../types/tf_types'

const NOT_LOOKING = 'Not Looking'
const NOT_A_CANDIDATE = 'Not a Candidate'

export function CandidatesPage() {
  const { options, notify, notifyError } = useApp()
  const [params, setParams] = useSearchParams()

  const [rows, setRows] = useState<CandidateRow[]>([])
  const [total, setTotal] = useState(0)
  const [untriaged, setUntriaged] = useState(0)
  const [catalog, setCatalog] = useState<SkillCatalogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<{ message: string; technical?: string } | null>(null)

  const query = params.get('q') ?? ''
  const selectedSkills = useMemo(
    () => (params.get('skills') ?? '').split(',').map((s) => s.trim()).filter(Boolean),
    [params],
  )
  const withResumeOnly = params.get('resume') === '1'
  const [searchBox, setSearchBox] = useState(query)

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next)
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await tfApi.fetchCandidates({
        q: query || undefined,
        skills: selectedSkills.length ? selectedSkills.join(',') : undefined,
        with_resume_only: withResumeOnly || undefined,
      })
      setRows(result.Items)
      setTotal(result.Total)
      setUntriaged(result.Untriaged)
    } catch (err: any) {
      setError({ message: err?.detail?.message ?? String(err), technical: err?.detail?.technical })
    } finally {
      setLoading(false)
    }
  }, [query, selectedSkills, withResumeOnly])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void tfApi.fetchSkillCatalog().then((r) => setCatalog(r.Items)).catch(() => undefined)
  }, [rows.length])

  const toggleSkill = (skill: string) => {
    const next = selectedSkills.includes(skill)
      ? selectedSkills.filter((s) => s !== skill)
      : [...selectedSkills, skill]
    setParam('skills', next.length ? next.join(',') : null)
  }

  const setStatus = (row: CandidateRow) => async (next: string | null) => {
    try {
      await tfApi.setCandidateStatus(row.ContactId, next)
      notify({
        tone: 'ok',
        title: 'Candidate status set',
        body:
          next === NOT_LOOKING || next === NOT_A_CANDIDATE
            ? `${row.DisplayName} is off the candidate list.`
            : `${row.DisplayName} — ${next ?? 'not set'}.`,
      })
      await load()
    } catch (err) {
      notifyError('Could not set the candidate status', err)
      throw err
    }
  }

  const statusOptions = useMemo(
    () => options('CANDIDATE_STATUS').map((v) => ({ value: v.ValueLabel, label: v.ValueLabel })),
    [options],
  )

  const withSkills = rows.filter((r) => r.SkillCount > 0).length
  const withResumes = rows.filter((r) => r.ResumeCount > 0).length

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Talent / Candidates"
        title="Candidates"
        description="Everybody except Not Looking and Not a Candidate. Attach a resume and its skills become searchable here."
        actions={
          <button className="btn secondary" type="button" onClick={() => void load()}>
            <span className="material-symbols-outlined">refresh</span>Refresh
          </button>
        }
      />

      <section className="page-toolbar" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div className="global-search" style={{ maxWidth: 340 }}>
          <span className="material-symbols-outlined" aria-hidden="true">search</span>
          <input
            type="search"
            value={searchBox}
            aria-label="Search candidates, resumes and skills"
            placeholder="Search names, roles and resume text…"
            onChange={(e) => setSearchBox(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setParam('q', searchBox || null) }}
          />
        </div>
        <button
          type="button"
          className={`chip${withResumeOnly ? ' on' : ''}`}
          onClick={() => setParam('resume', withResumeOnly ? null : '1')}
        >
          Has a resume
        </button>
        {(selectedSkills.length > 0 || query) && (
          <button className="btn ghost sm" type="button" onClick={() => setParams(new URLSearchParams())}>
            <span className="material-symbols-outlined">close</span>Clear filters
          </button>
        )}
      </section>

      <div className="body-scroll">
        {error && (
          <ErrorState title="Could not load candidates" message={error.message}
                      technical={error.technical} onRetry={load} />
        )}

        {/* ---------------------------------------------------------- the skill catalogue */}
        {catalog.length > 0 && (
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="card-head">
              <span className="card-title">
                <span className="material-symbols-outlined">label</span>
                Search by skill
              </span>
            </div>
            <div className="card-body">
              {/* PICKED, NOT TYPED. A free-text skill box makes the recruiter guess the spelling
                  that was extracted; a list of what actually exists, with counts, means a click
                  is guaranteed to return somebody. */}
              <div className="chip-row">
                {catalog.slice(0, 40).map((s) => (
                  <button
                    key={s.SkillName}
                    type="button"
                    className={`chip${selectedSkills.includes(s.SkillName) ? ' on' : ''}`}
                    onClick={() => toggleSkill(s.SkillName)}
                  >
                    {s.SkillName} <span className="cn">{s.ContactCount}</span>
                  </button>
                ))}
              </div>
              {selectedSkills.length > 1 && (
                <p className="form-hint" style={{ marginTop: 8 }}>
                  Showing people with <b>all {selectedSkills.length}</b> — asking for two skills
                  describes one person, so returning everyone with either would make the filter
                  useless exactly when it starts to matter.
                </p>
              )}
            </div>
          </div>
        )}

        {catalog.length === 0 && !loading && (
          <div className="banner info">
            <span className="material-symbols-outlined">description</span>
            <div>
              <b>No skills have been extracted yet.</b>
              <div style={{ marginTop: 4 }}>
                Attach a resume on somebody's profile — Files tab — and its skills and
                qualifications are read out and become searchable here. PDF, Word and text files are
                read directly; a scanned PDF has no text to read and will say so.
              </div>
            </div>
          </div>
        )}

        {untriaged > 0 && (
          <div className="banner info">
            <span className="material-symbols-outlined">how_to_reg</span>
            <div>
              <b>{untriaged} {untriaged === 1 ? 'person has' : 'people have'} no candidate status yet</b>,
              so they are listed here. Set <em>Not a Candidate</em> or <em>Not Looking</em> on anyone
              who should not be — nobody is hidden until you say so.
            </div>
          </div>
        )}

        {loading ? <Spinner /> : rows.length === 0 ? (
          <EmptyState
            icon="badge"
            title="No candidates match"
            message={
              selectedSkills.length || query
                ? 'Nothing matched those filters. Skills come from attached resumes, so a candidate with no resume will not match a skill filter.'
                : 'Everybody has been marked Not Looking or Not a Candidate.'
            }
          />
        ) : (
          <div className="table-wrap">
            <div className="table-scroll">
              <table className="dt">
                <caption className="sr-only">
                  Candidates with their status, extracted skills, resumes and experience.
                </caption>
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Candidate Status</th>
                    <th>Role</th>
                    <th>Experience</th>
                    <th>Skills</th>
                    <th>Resume</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.ContactId}>
                      <td>
                        <div className="person-cell">
                          <Avatar name={row.DisplayName} />
                          <div>
                            <Link className="person-name" to={`/relationships/people/${row.ContactId}`}>
                              {row.DisplayName}
                            </Link>
                            <div className="person-sub">
                              {[row.JobTitle, row.OrganizationName].filter(Boolean).join(' · ') || '—'}
                            </div>
                            {/* Why they matched, not just that they did. */}
                            {row.MatchedOn.length > 0 && (
                              <div className="person-sub">
                                matched their {row.MatchedOn.join(' and ')}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ minWidth: 160 }}>
                        <InlineEditCell
                          value={row.CandidateStatus}
                          type="select"
                          options={statusOptions}
                          placeholder="Not triaged"
                          ariaLabel={`Candidate status for ${row.DisplayName}`}
                          onCommit={setStatus(row)}
                        />
                      </td>
                      <td><span className="person-sub">{row.CurrentRole ?? '—'}</span></td>
                      <td className="num">
                        <span className="person-sub">
                          {row.YearsExperience ? `${row.YearsExperience} yrs` : '—'}
                        </span>
                      </td>
                      <td style={{ maxWidth: 320 }}>
                        {row.Skills.length === 0 ? (
                          <span className="person-sub">
                            {row.ResumeCount ? 'No skills read from their resume' : 'No resume'}
                          </span>
                        ) : (
                          <div className="chip-row">
                            {row.Skills.slice(0, 6).map((s) => (
                              <Tag key={s}>{s}</Tag>
                            ))}
                            {row.Skills.length > 6 && (
                              <span className="person-sub">+{row.Skills.length - 6} more</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {row.ResumeCount === 0 ? (
                          <span className="person-sub">—</span>
                        ) : (
                          <span className={`badge b-${row.HasSearchableResume ? 'strong' : 'developing'}`}>
                            <span className="dot" />
                            {/* Attached and readable are different states. A scanned PDF attaches
                                perfectly and contributes nothing to search. */}
                            {row.HasSearchableResume ? `${row.ResumeCount} readable` : 'Not readable'}
                          </span>
                        )}
                      </td>
                      <td>
                        <Link className="btn ghost sm" to={`/relationships/people/${row.ContactId}?tab=files`}>
                          <span className="material-symbols-outlined">upload_file</span>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-foot">
              <span>
                {total} candidate{total === 1 ? '' : 's'} · {withResumes} with a resume ·{' '}
                {withSkills} with extracted skills
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
